// Client-side generator for a shareable "Lo sai che..." card image.
// Draws the short text on a warm-hued full-frame canvas in Stories format
// (1080×1920) and hands the resulting PNG to `navigator.share` when
// available, or falls back to a plain browser download.
//
// The image is 100% static: no user data, no interactive elements. Only the
// short hook of the current card is used — the deep_dive stays inside the
// app on purpose.

const W = 1080;
const H = 1920;

// Vertical safe area for the main text (between the top progress-bar-ish
// margin and the brand pill at the bottom). Keeps text away from device
// clipping and profile UI overlays in Instagram Stories.
const SAFE_TOP = 220;
const SAFE_BOTTOM = 460;  // enough room for the mascot + brand text footer
const SIDE_PADDING = 120;

async function ensureFonts() {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    // Warm up the two font sizes we use so canvas has real glyphs to render.
    await Promise.all([
      document.fonts.load("900 96px \"Nunito\""),
      document.fonts.load("700 42px \"DM Sans\""),
    ]);
  } catch {
    /* not fatal — canvas will fall back to system-ui */
  }
}

// Cache the mascot image so we don't refetch on every share.
let mascotImagePromise = null;
function loadMascotImage() {
  if (mascotImagePromise) return mascotImagePromise;
  mascotImagePromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // graceful — pill will render without mascot
    img.src = "/assets/boardy-energico.png";
  });
  return mascotImagePromise;
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = "";
  for (const w of words) {
    const next = current ? `${current} ${w}` : w;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// Pick a font size that lets the whole text fit in the safe area.
function fitTypography(ctx, text, maxWidth, maxHeight) {
  const sizes = [104, 96, 88, 80, 72, 64, 58, 52, 48];
  for (const size of sizes) {
    ctx.font = `900 ${size}px "Nunito", system-ui, sans-serif`;
    const lineHeight = Math.round(size * 1.15);
    const lines = wrapLines(ctx, text, maxWidth);
    if (lines.length * lineHeight <= maxHeight) {
      return { size, lineHeight, lines };
    }
  }
  // last resort — use the smallest size even if it overflows a little
  const size = 44;
  ctx.font = `900 ${size}px "Nunito", system-ui, sans-serif`;
  return {
    size,
    lineHeight: Math.round(size * 1.15),
    lines: wrapLines(ctx, text, maxWidth),
  };
}

function drawBrandFooter(ctx, appName, mascotImage) {
  // New layout: Boardy is the hero of the footer. He sits large and
  // centered above a small, plain text label. No more pill wrapping.
  const mascotSize = 220;
  const labelFontSize = 40;
  const gap = 24;

  const centerX = W / 2;
  // Anchor the footer group so the label baseline lands SAFE_BOTTOM/2 above
  // the frame bottom — leaves room for OS UI overlays on Stories.
  const labelY = H - 110;
  const mascotBottomY = labelY - labelFontSize - gap;
  const mascotY = mascotBottomY - mascotSize;

  if (mascotImage) {
    const ratio = mascotImage.naturalWidth && mascotImage.naturalHeight
      ? mascotImage.naturalWidth / mascotImage.naturalHeight
      : 1;
    const drawW = ratio >= 1 ? mascotSize : mascotSize * ratio;
    const drawH = ratio >= 1 ? mascotSize / ratio : mascotSize;
    // Soft white halo behind the mascot so it stays legible on darker hues.
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.18)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 6;
    ctx.drawImage(
      mascotImage,
      centerX - drawW / 2,
      mascotY + (mascotSize - drawH) / 2,
      drawW,
      drawH
    );
    ctx.restore();
  }

  // Brand text — small, discreet, sits under the mascot in white on the
  // warm background. Boardy remains the visual anchor.
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = `700 ${labelFontSize}px "DM Sans", system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.shadowColor = "rgba(0,0,0,0.18)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fillText(appName, centerX, labelY);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

async function renderCanvas({ shortText, hue, appName, mascotImage }) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // 1) Solid warm background
  ctx.fillStyle = hue;
  ctx.fillRect(0, 0, W, H);

  // 2) Subtle darker overlay at the bottom for legibility of the brand pill
  const gradient = ctx.createLinearGradient(0, H * 0.65, 0, H);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, "rgba(0,0,0,0.15)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, W, H);

  // 3) Main text — auto-fit typography inside the safe area
  const maxTextWidth = W - SIDE_PADDING * 2;
  const maxTextHeight = H - SAFE_TOP - SAFE_BOTTOM;
  const { lineHeight, lines } = fitTypography(ctx, shortText, maxTextWidth, maxTextHeight);

  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.12)";
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 4;

  const totalTextHeight = lines.length * lineHeight;
  const startY = SAFE_TOP + (maxTextHeight - totalTextHeight) / 2 + lineHeight / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, startY + i * lineHeight);
  });

  // reset shadow before drawing the pill
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // 4) Brand footer (bottom-center) — Boardy large, brand text small below
  drawBrandFooter(ctx, appName, mascotImage);

  return canvas;
}

async function canvasToPngFile(canvas, filename) {
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("canvas.toBlob failed");
  return new File([blob], filename, { type: "image/png" });
}

function fallbackDownload(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ----- public API -----

// Detect whether the current browser can share files via the Web Share API.
// Exported so the UI can hide the "download-only" fallback hint on devices
// that will get the real native sheet.
export function canNativelyShareFiles() {
  if (typeof navigator === "undefined") return false;
  if (!navigator.share || !navigator.canShare) return false;
  try {
    // Probe with a 1-byte dummy file — same MIME type we'll share.
    const probe = new File([new Uint8Array([0])], "probe.png", { type: "image/png" });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export async function shareCardImage({ cardId, shortText, hue, appName = "ExchangePlan", shareTitle, shareText }) {
  const [, mascotImage] = await Promise.all([ensureFonts(), loadMascotImage()]);
  const canvas = await renderCanvas({ shortText, hue, appName, mascotImage });
  const file = await canvasToPngFile(canvas, `exchangeplan-${cardId}.png`);

  const hasShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const hasCanShare = typeof navigator !== "undefined" && typeof navigator.canShare === "function";

  // Preferred path: Web Share API Level 2 with files — opens the native OS
  // sheet (Instagram Stories, WhatsApp, Messages, Save Image, …).
  //
  // We attempt navigator.share whenever it exists. If the browser exposes
  // canShare we honour it as a pre-flight, but we ALSO try the call blind
  // when canShare is missing (older Safari/Chrome mobile builds) — the
  // share promise itself will reject cleanly on unsupported payloads.
  if (hasShare) {
    let filesAccepted = true;
    if (hasCanShare) {
      try {
        filesAccepted = navigator.canShare({ files: [file] });
      } catch {
        // Some engines throw on canShare with files even when share works.
        filesAccepted = true;
      }
    }

    if (filesAccepted) {
      try {
        await navigator.share({
          files: [file],
          title: shareTitle,
          text: shareText,
        });
        return { shared: true, method: "web-share-files" };
      } catch (err) {
        if (err && err.name === "AbortError") {
          return { shared: false, cancelled: true, method: "web-share-files" };
        }
        // The share call itself rejected (permission denied, unsupported,
        // rate limit…). Fall through to the download fallback so the user
        // still ends up with their image.
        console.warn("[shareCardImage] navigator.share failed:", err);
      }
    }
  }

  // No file-share support (or share was rejected non-abort) → download.
  fallbackDownload(file);
  return {
    shared: false,
    downloaded: true,
    method: "download-fallback",
    reason: !hasShare ? "no-web-share-api" : "share-rejected",
  };
}
